import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { formatPrice, calculateSavingsPercentage } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import type { Product } from '@shared/schema';

interface ProductRowCardProps {
  product: Product;
}

export function ProductRowCard({ product }: ProductRowCardProps) {
  const [imageError, setImageError] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);
  const isMobile = useIsMobile();
  const savingsPercent = calculateSavingsPercentage(product.retailPrice, product.ourPrice);
  
  // Trigger random blinking effect every 5-8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 2000); // Blink for 2 seconds
    }, Math.random() * 3000 + 5000); // Random interval between 5-8 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div 
      whileHover={{ scale: 1.02, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
      transition={{ duration: 0.2 }}
      className="relative bg-gradient-to-r from-blue-50/30 to-purple-50/30 border-4 border-gray-300 rounded-lg overflow-hidden shadow-sm hover:shadow-lg"
    >
      
      <div className={`flex ${isMobile ? 'h-auto' : 'h-[200px]'}`}>
        {/* Product Image */}
        <div className={`${isMobile ? 'w-24 h-24' : 'w-[200px] h-[200px]'} flex-shrink-0 border-r-2 border-gray-200 bg-gray-50 flex items-center justify-center`}>
          {!imageError && product.imageUrl ? (
            <img 
              src={product.imageUrl} 
              alt={product.description}
              className="max-w-full max-h-full object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <span className="text-gray-400 text-2xl">🛒</span>
            </div>
          )}
        </div>
        
        {/* Product Info */}
        <div className="flex-1 flex">
          {/* Left side: Product details */}
          <div className={`flex-1 flex ${isMobile ? 'px-2 py-2' : 'px-4 py-3'}`}>
            {/* Description and Size columns */}
            <div className="flex-1 flex items-center">
              <div className="flex-1">
                <h3 className={`font-semibold text-gray-900 ${isMobile ? 'text-sm' : 'text-2xl'}`}>
                  {product.description}
                </h3>
                <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-lg'}`}>
                  {product.size}
                </p>
              </div>
            </div>
            
            {/* Retail Price */}
            <div className={`flex flex-col justify-center ${isMobile ? 'px-2' : 'px-4'}`}>
              <div className="text-center">
                <div className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 ${isMobile ? '' : 'mb-1'}`}>Retail</div>
                <div className={`${isMobile ? 'text-base' : 'text-2xl'} text-red-500 line-through font-bold`}>
                  {formatPrice(product.retailPrice)}
                </div>
              </div>
            </div>
          </div>

          {/* Right side: Our Price - Full height */}
          <div className={`${isMobile ? 'w-24' : 'w-48'} bg-emerald-50 border-l-4 border-emerald-200 flex flex-col items-center justify-center ${isMobile ? 'px-1' : 'px-4'}`}>
            <span className={`${isMobile ? 'text-[10px]' : 'text-sm'} text-gray-600 font-medium ${isMobile ? '' : 'mb-2'}`}>
              Our Price
            </span>
            <motion.div
              animate={isBlinking ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.6, repeat: isBlinking ? 3 : 0 }}
              className={`${isMobile ? 'text-base' : 'text-3xl'} font-bold ${
                isBlinking 
                  ? 'text-emerald-600' 
                  : 'text-emerald-700'
              }`}
            >
              {formatPrice(product.ourPrice)}
            </motion.div>
            {savingsPercent > 0 && (
              <span className={`${isMobile ? 'text-[10px]' : 'text-sm'} text-emerald-600 font-bold ${isMobile ? '' : 'mt-2'}`}>
                {isMobile ? `${savingsPercent}%` : `Save ${savingsPercent}%`}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}